import { useEffect, useRef, useState } from 'react'
import { Order } from '../../../proto/generated/order'
import { Security } from '../../../proto/generated/security'
import { Snapshot } from '../../../reducer/security.reducer'
import { TradeOrder } from '../../../reducer/strategyBuilder.reducer'
import store from '../../../store'
import { getSecurityKey } from '../../../util/security'
import { getNonAuxiliaryLegsInBatch } from '../../../util/conditionalEntry'
import { snapshotTickEquals } from '../../../util/snapshotTick'
import { AnalyzeGroupedPosition } from '../../../reducer/positions.reducer'

export type BasketWeightedMarketPrices = {
    basketWeightedLastPrice: number
    basketAvgVolume: number
}

export type BasketWeightedMarketParams = {
    marketTickKeys: string[]
    firstLegSnapshotKey: string
    tradeOrders: TradeOrder[]
    placedOrders: Order[]
    basketId: string
    effectiveBatchId: string
    icebergLegMult: number
    mode: string
    basketPosition?: AnalyzeGroupedPosition
    firstLegSecurity?: Security
}

const pickSnapshots = (keys: string[]): Record<string, Snapshot> => {
    const all = store.getState().securityStore.snapshots
    const result: Record<string, Snapshot> = {}
    for (const key of keys) {
        if (all[key]) result[key] = all[key]
    }
    return result
}

function computeWeightedPrices(
    allSnapshots: Record<string, Snapshot>,
    params: BasketWeightedMarketParams
): BasketWeightedMarketPrices {
    const {
        firstLegSnapshotKey,
        tradeOrders,
        placedOrders,
        basketId,
        effectiveBatchId,
        icebergLegMult,
        mode,
        basketPosition,
    } = params
    const firstLegSnapshot = firstLegSnapshotKey ? allSnapshots[firstLegSnapshotKey] : undefined

    if (effectiveBatchId && basketId) {
        const placedMains = getNonAuxiliaryLegsInBatch(placedOrders || [], basketId, effectiveBatchId)
        if (placedMains.length > 0) {
            const qtyByKey = new Map<string, number>()
            for (const o of placedMains) {
                if (!o.security?.code) continue
                const key = getSecurityKey(o.security.exchange, o.security.code)
                qtyByKey.set(key, (qtyByKey.get(key) ?? 0) + Math.abs(o.quantity ?? 0))
            }
            let wNum = 0
            let wDen = 0
            let volNum = 0
            let volDen = 0
            for (const [key, w] of qtyByKey) {
                if (w <= 0) continue
                const snap = allSnapshots[key]
                const ltp = snap?.lastTradedPrice ?? 0
                const vol = snap?.volume ?? 0
                if (ltp > 0) {
                    wNum += ltp * w
                    wDen += w
                }
                volNum += vol * w
                volDen += w
            }
            if (wDen > 0 || volDen > 0) {
                return {
                    basketWeightedLastPrice:
                        wDen > 0 ? wNum / wDen : firstLegSnapshot?.lastTradedPrice ?? 0,
                    basketAvgVolume: volDen > 0 ? volNum / volDen : firstLegSnapshot?.volume ?? 0,
                }
            }
        }
    }

    const sel = tradeOrders.filter((t) => t.selected)
    const lines = sel.length ? sel : tradeOrders
    let wNum = 0
    let wDen = 0
    let volSum = 0
    let volN = 0
    for (const line of lines) {
        if (!line.security?.code) continue
        const key = getSecurityKey(line.security.exchange, line.security.code)
        const snap = allSnapshots[key]
        const ltp = snap?.lastTradedPrice ?? 0
        const vol = snap?.volume ?? 0
        const w = Math.abs((line.quantity ?? 0) * icebergLegMult)
        if (w > 0 && ltp > 0) {
            wNum += ltp * w
            wDen += w
        }
        volSum += vol
        volN += 1
    }
    if (
        lines.length === 0 &&
        (mode === 'analyze' || mode === 'squareOff') &&
        basketPosition?.children?.length
    ) {
        for (const child of basketPosition.children) {
            if (!child.security?.code) continue
            const key = getSecurityKey(child.security.exchange, child.security.code)
            const snap = allSnapshots[key]
            const ltp = snap?.lastTradedPrice ?? 0
            const vol = snap?.volume ?? 0
            const w = Math.abs(child.quantity ?? 0)
            if (w > 0 && ltp > 0) {
                wNum += ltp * w
                wDen += w
            }
            volSum += vol
            volN += 1
        }
    }
    return {
        basketWeightedLastPrice: wDen > 0 ? wNum / wDen : firstLegSnapshot?.lastTradedPrice ?? 0,
        basketAvgVolume: volN > 0 ? volSum / volN : firstLegSnapshot?.volume ?? 0,
    }
}

const weightedPricesEqual = (a: BasketWeightedMarketPrices, b: BasketWeightedMarketPrices) =>
    a.basketWeightedLastPrice === b.basketWeightedLastPrice &&
    a.basketAvgVolume === b.basketAvgVolume

export function useBasketWeightedMarketPrices(params: BasketWeightedMarketParams): BasketWeightedMarketPrices {
    const paramsRef = useRef(params)
    paramsRef.current = params

    const keysKey = params.marketTickKeys.join('\0')
    const keysRef = useRef(params.marketTickKeys)
    keysRef.current = params.marketTickKeys
    const snapshotsRef = useRef<Record<string, Snapshot>>({})

    const [prices, setPrices] = useState<BasketWeightedMarketPrices>(() =>
        computeWeightedPrices(pickSnapshots(keysRef.current), paramsRef.current)
    )

    useEffect(() => {
        const snapshots = pickSnapshots(keysRef.current)
        snapshotsRef.current = snapshots
        const next = computeWeightedPrices(snapshots, paramsRef.current)
        setPrices((prev) => (weightedPricesEqual(prev, next) ? prev : next))
    }, [
        keysKey,
        params.tradeOrders,
        params.placedOrders,
        params.basketId,
        params.effectiveBatchId,
        params.icebergLegMult,
        params.mode,
        params.basketPosition,
        params.firstLegSnapshotKey,
    ])

    useEffect(() => {
        if (!keysRef.current.length) return

        return store.subscribe(() => {
            const nextSnapshots = pickSnapshots(keysRef.current)
            let tickChanged = false
            for (const key of keysRef.current) {
                if (!snapshotTickEquals(snapshotsRef.current[key], nextSnapshots[key])) {
                    tickChanged = true
                    break
                }
            }
            if (!tickChanged) return
            snapshotsRef.current = nextSnapshots
            const next = computeWeightedPrices(nextSnapshots, paramsRef.current)
            setPrices((prev) => (weightedPricesEqual(prev, next) ? prev : next))
        })
    }, [keysKey])

    return prices
}
